// @givmo/board — the ordered sessions a meeting is made of, and the closed-session boundary.
//
// ══ THE HARD BOUNDARY ══════════════════════════════════════════════════════════════════════════
//
// An executive session is a MODELED STATE with its own read rule, applied at the substrate: a reader
// withheld from closed-session minutes who could read the motions instead would defeat the segregation
// in one step, so the backend scopes the segments, their participants, AND the motions made inside
// them. Denial is ABSENCE — a closed session the reader may not see answers exactly as a bogus id
// does.
//
// ⚠ **SO THIS PANEL RENDERS WHAT ARRIVED AND IMPLIES NOTHING ABOUT WHAT DID NOT.** There is no
// "n sessions hidden", no greyed placeholder row, no count compared against a total, and no
// `{present:false}` variant anywhere in the list — the module's established absence semantics
// (`records-decode.ts`'s `recordPointers()`, pinned by an equality on the rendered page). A
// placeholder would disclose the existence of the very thing the record store withheld, which is the
// leak the segregation exists to prevent.
//
// ⚠ **AND NO ORDINAL IS EVER SHOWN.** `position` is contiguous in the database and deliberately not
// projected: handing a reader sessions numbered 1, 2 and 4 tells them exactly how many closed
// sessions there were and where they fell in the evening — the same arithmetic leak the record core
// found in a post-filtered page's `meta.total`. So this list renders in the order it arrived and
// numbers nothing.
//
// Two consequences for the write side, both inherited rather than invented here:
//
//   * **REORDER TAKES THE FULL ORDERED LIST OF IDS.** A caller who cannot see every session
//     structurally cannot produce that list, so the scoping fences the reorder with no check of its
//     own. Same shape as the agenda and packet reorders, for the same deferred-constraint reason.
//   * **`segment_type` IS NOT EDITABLE ANYWHERE.** Changing it would change who may read everything
//     recorded inside, and a field flip on a PATCH is how a closed session gets published by accident.
//     It is absent from the backend's update command, its wire model and its service — so the edit
//     dialog here does not offer it either, and that is a deliberate absence rather than an oversight.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  isGivmoError,
  type BoardMeetingSegmentOut,
  type BoardMeetingSegmentParticipantOut,
  type BoardMeetingSegmentType,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Icon,
  IconButton,
  RowActions,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FieldNote } from '../../components/field-note';
import { displayInstant } from '../../lib/display';
import {
  extractParticipants,
  extractSegments,
  extractSteppedOut,
  isOpenSession,
  seatPickerLabel,
  seatsServingOn,
  segmentHeading,
  segmentTypeLabel,
  segmentTypeTone,
  type MeetingDay,
} from './in-meeting-decode';
import {
  closedSessionWords as words,
  purposeChoiceOf,
  purposeToRecord,
  type PurposeChoice,
} from './closed-session-words';
import { useBoardOperations } from '../../board-runtime';
import { officerRolesWords as roles } from '../roster/officer-roles-words';

const SEGMENT_TYPE_OPTIONS: readonly { value: BoardMeetingSegmentType; label: string }[] = [
  { value: 'open_session', label: 'Open session' },
  { value: 'executive_session', label: 'Executive session' },
];

export interface SessionsPanelProps {
  orgRef: string;
  meetingId: string;
  /** The WHOLE roster, former seats included (`include_removed`). */
  roster: readonly OrgBoardMemberOut[];
  /** The meeting's day: the participant picker offers the seats that served on it (CBG-FIX41). */
  servedOn: MeetingDay | null;
  /**
   * CBG-FIX104: the meeting is a committee's (its own `committee_id`). Recording a seat opens the
   * session to it only where the seat is on the board, or, at a committee's meeting, on the
   * committee, so the picker's note says which (D-CBG-25 as amended).
   */
  committeeMeeting: boolean;
  readOnly: boolean;
}

export function SessionsPanel({
  orgRef,
  meetingId,
  roster,
  servedOn,
  committeeMeeting,
  readOnly,
}: SessionsPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BoardMeetingSegmentOut | null>(null);
  const [removing, setRemoving] = useState<BoardMeetingSegmentOut | null>(null);
  const [addingParticipantTo, setAddingParticipantTo] = useState<BoardMeetingSegmentOut | null>(
    null,
  );

  const segmentsOptions = operations.officerListBoardMeetingSegmentsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const segmentsQuery = useQuery({ ...segmentsOptions, select: extractSegments });
  const segments = segmentsQuery.data ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: segmentsOptions.queryKey });

  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await operations.officerReorderBoardMeetingSegments({
        path: { org_ref: orgRef, meeting_id: meetingId },
        // The WHOLE list, in the new order — never one session's new position. The backend's position
        // constraint is deferred precisely because two entries exchanging places are briefly equal
        // under either ordering of two updates.
        body: { segment_ids: orderedIds },
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidate(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught)
          ? caught.message
          : 'We couldn’t reorder the sessions. Please try again.',
      );
    },
  });

  function move(index: number, delta: number) {
    const next = [...segments];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const moved = next[index];
    if (moved === undefined) return;
    next.splice(index, 1);
    next.splice(target, 0, moved);
    reorder.mutate(next.map((segment) => segment.id));
  }

  const removeParticipant = useMutation({
    mutationFn: async (input: { segmentId: string; participantId: string }) => {
      await operations.officerRemoveBoardSegmentParticipant({
        path: {
          org_ref: orgRef,
          meeting_id: meetingId,
          segment_id: input.segmentId,
          participant_id: input.participantId,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidate(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that entry. Please try again.',
      );
    },
  });

  return (
    <Card testId="sessions-panel">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              How the meeting ran
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {words.sessionsLead}
            </p>
          </div>
          {!readOnly ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setAdding(true)}
              testId="sessions-add"
            >
              Add a session
            </Button>
          ) : null}
        </div>

        {segments.length === 0 ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            data-testid="sessions-empty"
          >
            {segmentsQuery.isPending
              ? 'Loading…'
              : 'No sessions recorded. Most meetings need none — add one when the board went into an executive session, or when you want the evening recorded in parts.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-12" data-testid="sessions-list">
            {segments.map((segment, index) => {
              const participants = extractParticipants(segment);
              const closed = !isOpenSession(segment);
              return (
                <li
                  key={segment.id}
                  className="flex flex-col gap-10 rounded-lg border border-border-subtle p-12"
                  data-testid={`session-${segment.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-12">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-wrap items-center gap-8">
                        {/* No ordinal — see the header. The order is the list's own order. */}
                        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {segmentHeading(segment)}
                        </span>
                        <Badge tone={segmentTypeTone(segment.segment_type)}>
                          {segmentTypeLabel(segment.segment_type)}
                        </Badge>
                      </div>
                      {segment.entered_at || segment.exited_at ? (
                        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                          {segment.entered_at
                            ? `Entered ${displayInstant(segment.entered_at)}`
                            : null}
                          {segment.entered_at && segment.exited_at ? ' · ' : null}
                          {segment.exited_at ? `Left ${displayInstant(segment.exited_at)}` : null}
                        </span>
                      ) : null}
                      {segment.purpose_note ? (
                        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                          {segment.purpose_note}
                        </p>
                      ) : null}
                    </div>

                    {!readOnly ? (
                      <div className="flex items-center gap-6">
                        <IconButton
                          aria-label={`Move “${segmentHeading(segment)}” earlier`}
                          tone="ghost"
                          size="sm"
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => move(index, -1)}
                          testId={`session-up-${segment.id}`}
                        >
                          <Icon icon={ChevronUp} size={16} aria-hidden />
                        </IconButton>
                        <IconButton
                          aria-label={`Move “${segmentHeading(segment)}” later`}
                          tone="ghost"
                          size="sm"
                          disabled={index === segments.length - 1 || reorder.isPending}
                          onClick={() => move(index, 1)}
                          testId={`session-down-${segment.id}`}
                        >
                          <Icon icon={ChevronDown} size={16} aria-hidden />
                        </IconButton>
                        <RowActions
                          label={`Actions for ${segmentHeading(segment)}`}
                          testId={`session-actions-${segment.id}`}
                          actions={[
                            {
                              label: 'Edit this session',
                              testId: `session-edit-${segment.id}`,
                              onSelect: () => setEditing(segment),
                            },
                            {
                              label: 'Remove this session',
                              testId: `session-remove-${segment.id}`,
                              destructive: true,
                              onSelect: () => setRemoving(segment),
                            },
                          ]}
                        />
                      </div>
                    ) : null}
                  </div>

                  {closed ? (
                    <div
                      className="flex flex-col gap-8"
                      data-testid={`session-people-${segment.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-8">
                        <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                          In the room
                        </span>
                        {!readOnly ? (
                          <Button
                            appearance="saas"
                            tone="secondary"
                            onClick={() => setAddingParticipantTo(segment)}
                            testId={`session-add-person-${segment.id}`}
                          >
                            Add someone
                          </Button>
                        ) : null}
                      </div>
                      {participants.length === 0 ? (
                        <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                          {/* The instruction is for whoever can list them: a read-only record
                              (closed, or a reader) states the fact alone. CBG-FIX104 (row 1.3,
                              approved 2026-10-17): the list is the record of who was in the room,
                              and not everyone on it reads the session (D-CBG-25 as amended). */}
                          {readOnly
                            ? 'Nobody recorded yet.'
                            : 'Nobody recorded yet. Everyone who was in this session — directors and anyone the board invited in — needs to be listed here, because this list is the record of who was in the room.'}
                        </span>
                      ) : (
                        <ul className="flex flex-col gap-6">
                          {participants.map((participant) => (
                            <ParticipantRow
                              key={participant.id}
                              participant={participant}
                              readOnly={readOnly}
                              onRemove={() =>
                                removeParticipant.mutate({
                                  segmentId: segment.id,
                                  participantId: participant.id,
                                })
                              }
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>

      {adding ? (
        <SessionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          segment={null}
          roster={seatsServingOn(roster, servedOn)}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void invalidate();
          }}
        />
      ) : null}

      {editing !== null ? (
        <SessionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          segment={editing}
          roster={seatsServingOn(roster, servedOn)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}

      {addingParticipantTo !== null ? (
        <ParticipantDialog
          orgRef={orgRef}
          meetingId={meetingId}
          segment={addingParticipantTo}
          roster={seatsServingOn(roster, servedOn)}
          committeeMeeting={committeeMeeting}
          onClose={() => setAddingParticipantTo(null)}
          onSaved={() => {
            setAddingParticipantTo(null);
            void invalidate();
          }}
        />
      ) : null}

      {removing !== null ? (
        <RemoveSessionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          segment={removing}
          onClose={() => setRemoving(null)}
          onRemoved={() => {
            setRemoving(null);
            void invalidate();
          }}
        />
      ) : null}
    </Card>
  );
}

function ParticipantRow({
  participant,
  readOnly,
  onRemove,
}: {
  participant: BoardMeetingSegmentParticipantOut;
  readOnly: boolean;
  onRemove: () => void;
}) {
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-8"
      data-testid={`session-person-${participant.id}`}
    >
      <div className="flex flex-col">
        {/* The name is recorded VERBATIM on the backend rather than resolved from the roster, because
            the roster is editable and this is the record of who was in the room. So it is shown as
            stored, not looked up. */}
        <span className="font-sans text-s13 font-w400 tracking-givmo text-text-strong">
          {participant.display_name}
        </span>
        {participant.participant_note ? (
          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            {participant.participant_note}
          </span>
        ) : null}
      </div>
      {!readOnly ? (
        <Button
          appearance="saas"
          tone="secondary"
          onClick={onRemove}
          testId={`session-person-remove-${participant.id}`}
        >
          Remove
        </Button>
      ) : null}
    </li>
  );
}

/** One seat recorded as having stepped out of a closed session, as the dialog stages it. */
interface SteppedOutEntry {
  seatId: string;
  name: string;
  note: string;
}

function SessionDialog({
  orgRef,
  meetingId,
  segment,
  roster,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  segment: BoardMeetingSegmentOut | null;
  /** The seats that served on the meeting's day: who the stepped-out picker offers. */
  roster: readonly OrgBoardMemberOut[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const isEdit = segment !== null;
  const [segmentType, setSegmentType] = useState<BoardMeetingSegmentType>(
    (segment?.segment_type as BoardMeetingSegmentType | undefined) ?? 'open_session',
  );
  const [title, setTitle] = useState(segment?.title ?? '');
  const [purposeNote, setPurposeNote] = useState(segment?.purpose_note ?? '');
  // CBG-RC12-REC: a closed session's general purpose, and who stepped out of it for a conflict of
  // interest. Staged here and saved with the session in one write, as the whole list (the
  // backend reads both by presence).
  const [purpose, setPurpose] = useState<PurposeChoice>(() =>
    purposeChoiceOf(segment?.purpose_label),
  );
  const [steppedOut, setSteppedOut] = useState<SteppedOutEntry[]>(() =>
    segment === null
      ? []
      : extractSteppedOut(segment).map((row) => ({
          seatId: row.board_member_id,
          name: row.display_name,
          note: row.note ?? '',
        })),
  );
  const [error, setError] = useState<string | null>(null);

  // Positive on the executive kind, as `isOpenSession` is the other way round: only a closed
  // session has a public entry.
  const closed = isEdit ? !isOpenSession(segment) : segmentType === 'executive_session';
  const inTheRoom = new Set(
    segment === null
      ? []
      : extractParticipants(segment)
          .map((row) => row.board_member_id)
          .filter((id): id is string => typeof id === 'string'),
  );

  const save = useMutation({
    mutationFn: async () => {
      const shared = {
        title: title.trim().length > 0 ? title.trim() : null,
        purpose_note: purposeNote.trim().length > 0 ? purposeNote.trim() : null,
        ...(closed
          ? {
              purpose_label: purposeToRecord(purpose),
              stepped_out: steppedOut.map((entry) => ({
                board_member_id: entry.seatId,
                note: entry.note.trim().length > 0 ? entry.note.trim() : null,
              })),
            }
          : {}),
      };
      if (isEdit) {
        // ⚠ `segment_type` is deliberately NOT in this body — see the file header. Changing it would
        // change who may read everything recorded inside the session.
        await operations.officerUpdateBoardMeetingSegment({
          path: { org_ref: orgRef, meeting_id: meetingId, segment_id: segment.id },
          body: shared,
          throwOnError: true,
        });
        return;
      }
      await operations.officerCreateBoardMeetingSegment({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: { ...shared, segment_type: segmentType },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Session updated.' : 'Session added.');
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={isEdit ? 'Edit this session' : 'Add a session'}
        className="flex flex-col gap-16"
        testId="session-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isEdit ? 'Edit this session' : 'Add a session'}
        </Dialog.Title>

        {isEdit ? (
          <div className="flex flex-col gap-4" data-testid="session-type-fixed">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
              Kind of session
            </span>
            <Badge tone={segmentTypeTone(segment.segment_type)}>
              {segmentTypeLabel(segment.segment_type)}
            </Badge>
            <FieldNote>{words.kindFixedNote}</FieldNote>
          </div>
        ) : (
          <>
            <Select
              label="Kind of session"
              appearance="saas"
              value={segmentType}
              onValueChange={(next) =>
                next !== null && setSegmentType(next as BoardMeetingSegmentType)
              }
              options={SEGMENT_TYPE_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              testId="session-type"
            />
            <FieldNote testId="session-type-note">{words.kindNewNote}</FieldNote>
          </>
        )}

        {closed ? <GeneralPurposeField purpose={purpose} onChange={setPurpose} /> : null}

        <TextField
          label="Title (optional)"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="session-title"
        />

        <Textarea
          label="Why the board went into it (optional)"
          value={purposeNote}
          onChange={(event) => setPurposeNote(event.target.value)}
          rows={3}
          testId="session-purpose"
        />
        {closed ? <FieldNote testId="session-kept-note">{words.keptNote}</FieldNote> : null}

        {closed ? (
          <SteppedOutField
            entries={steppedOut}
            onChange={setSteppedOut}
            offered={roster.filter(
              (seat) =>
                !inTheRoom.has(seat.id) && !steppedOut.some((entry) => entry.seatId === seat.id),
            )}
          />
        ) : null}

        {error !== null ? <ErrorMessage testId="session-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="session-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="session-save"
          >
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** CBG-RC12-REC — a closed session's general purpose: one of the offered labels, or the board's own
 *  words under "Something else". The officer's words; Board never classifies a matter. */
function GeneralPurposeField({
  purpose,
  onChange,
}: {
  purpose: PurposeChoice;
  onChange: (next: PurposeChoice) => void;
}) {
  // The dialog's own fields, at its own spacing (the frame, r1-session): the note sits under the
  // choice as every field note in this dialog does, not tucked against it.
  return (
    <>
      <Select
        label={words.purposeLabel}
        appearance="saas"
        placeholder={words.purposePlaceholder}
        value={purpose.choice}
        onValueChange={(next) => next !== null && onChange({ ...purpose, choice: next })}
        options={[...words.purposeLabels, words.purposeSomethingElse].map((label) => ({
          value: label,
          label,
        }))}
        testId="session-purpose-label"
      />
      {purpose.choice === words.purposeSomethingElse ? (
        <TextField
          label={words.purposeOwnWords}
          value={purpose.ownWords}
          maxLength={120}
          onChange={(event) => onChange({ ...purpose, ownWords: event.target.value })}
          testId="session-purpose-own-words"
        />
      ) : null}
      <FieldNote testId="session-purpose-label-note">{words.purposeNote}</FieldNote>
    </>
  );
}

/** CBG-RC12-REC — who stepped out of a closed session for a conflict of interest. Each entry is a
 *  dependent row, so it carries a leading rule (the givmo-design skill's rule for one). */
function SteppedOutField({
  entries,
  onChange,
  offered,
}: {
  entries: readonly SteppedOutEntry[];
  onChange: (next: SteppedOutEntry[]) => void;
  /** Seats that served on the meeting's day, not in the session and not already listed. */
  offered: readonly OrgBoardMemberOut[];
}) {
  const [adding, setAdding] = useState(false);
  const [seatId, setSeatId] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const add = () => {
    const seat = offered.find((row) => row.id === seatId);
    if (seat === undefined) return;
    onChange([...entries, { seatId: seat.id, name: seat.display_name, note }]);
    setAdding(false);
    setSeatId(null);
    setNote('');
  };

  return (
    <div className="flex flex-col gap-8" data-testid="session-stepped-out">
      <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
        {words.steppedOutHeading}
      </span>
      {entries.map((entry) => (
        <div
          key={entry.seatId}
          className="flex flex-col gap-4 border-l-2 border-border-strong pl-12"
          data-testid={`session-stepped-out-${entry.seatId}`}
        >
          <div className="flex items-center justify-between gap-12">
            <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
              {entry.name}
            </span>
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              aria-label={words.steppedOutRemoveLabel(entry.name)}
              onClick={() => onChange(entries.filter((other) => other.seatId !== entry.seatId))}
              testId={`session-stepped-out-remove-${entry.seatId}`}
            >
              {words.steppedOutRemove}
            </Button>
          </div>
          {entry.note.trim().length > 0 ? (
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {entry.note}
            </span>
          ) : null}
        </div>
      ))}
      {adding ? (
        <div
          className="flex flex-col gap-8 border-l-2 border-border-strong pl-12"
          data-testid="session-stepped-out-form"
        >
          <Select
            label={words.steppedOutSeat}
            appearance="saas"
            placeholder={words.steppedOutSeatPlaceholder}
            value={seatId}
            onValueChange={(next) => setSeatId(next)}
            options={offered.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
            testId="session-stepped-out-seat"
          />
          <TextField
            label={words.steppedOutNoteField}
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            testId="session-stepped-out-note-field"
          />
          <div className="flex gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => {
                setAdding(false);
                setSeatId(null);
                setNote('');
              }}
              testId="session-stepped-out-cancel"
            >
              {words.steppedOutCancel}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              disabled={seatId === null}
              onClick={add}
              testId="session-stepped-out-confirm"
            >
              {words.steppedOutAddButton}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          appearance="saas"
          tone="secondary"
          className="self-start"
          onClick={() => setAdding(true)}
          testId="session-stepped-out-add"
        >
          {words.steppedOutAdd}
        </Button>
      )}
      <FieldNote testId="session-stepped-out-note">{words.steppedOutNote}</FieldNote>
    </div>
  );
}

function ParticipantDialog({
  orgRef,
  meetingId,
  segment,
  roster,
  committeeMeeting,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  segment: BoardMeetingSegmentOut;
  roster: readonly OrgBoardMemberOut[];
  committeeMeeting: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const [seatId, setSeatId] = useState<string | null>(null);
  const [participantName, setParticipantName] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardSegmentParticipant({
        path: { org_ref: orgRef, meeting_id: meetingId, segment_id: segment.id },
        body: {
          board_member_id: seatId,
          // A non-director the board invited in — counsel, the executive director — is recorded by
          // name with no seat. The backend carries a nullable seat and a verbatim name for exactly
          // this, so somebody who is not an account holder at all can still be recorded as present.
          participant_name: participantName.trim().length > 0 ? participantName.trim() : null,
          participant_note: note.trim().length > 0 ? note.trim() : null,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded.');
      onSaved();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  const canSave = seatId !== null || participantName.trim().length > 0;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record someone in this session"
        className="flex flex-col gap-16"
        testId="participant-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Who else was in this session
        </Dialog.Title>

        <Select
          // CBG-FIX104 (row 1.1, approved 2026-10-17): the list offers every seat that served on
          // the meeting's day, committee members who aren't on the board among them.
          label="A seat"
          appearance="saas"
          placeholder="Choose a seat"
          value={seatId}
          onValueChange={(next) => setSeatId(next)}
          options={roster.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="participant-seat"
        />
        <FieldNote testId="participant-seat-note">
          {/* Whoever is recorded in the room reads the session, director or not (CBG-YR3-ROLES),
              where the seat is on the board, or on the meeting's committee (CBG-FIX104). */}
          {roles.sessionParticipantNote(committeeMeeting)}
        </FieldNote>

        <TextField
          label="Or someone who does not hold a seat"
          placeholder="Their name"
          value={participantName}
          onChange={(event) => setParticipantName(event.target.value)}
          testId="participant-name"
        />
        <FieldNote>
          Counsel, your executive director, anyone the board invited in. Their name is recorded as
          you type it — this is the record of who was in the room.
        </FieldNote>

        <TextField
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId="participant-note"
        />

        {error !== null ? <ErrorMessage testId="participant-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="participant-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={!canSave || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="participant-save"
          >
            Record
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function RemoveSessionDialog({
  orgRef,
  meetingId,
  segment,
  onClose,
  onRemoved,
}: {
  orgRef: string;
  meetingId: string;
  segment: BoardMeetingSegmentOut;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const operations = useBoardOperations();
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async () => {
      await operations.officerDeleteBoardMeetingSegment({
        path: { org_ref: orgRef, meeting_id: meetingId, segment_id: segment.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Session removed.');
      onRemoved();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !remove.isPending && onClose()}>
      <Dialog.Content
        aria-label="Remove this session"
        className="flex flex-col gap-16"
        testId="session-remove-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Remove “{segmentHeading(segment)}”?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* A session holding a motion cannot be removed — the backend refuses, because a motion the
              board made is not a thing to delete as a side effect of tidying the shape of the evening.
              Said before the click rather than surfaced as a bare error after it. */}
          If any motion was made in this session, you will not be able to remove it — a motion the
          board made is not removed by rearranging the evening. Move the motion first if that is
          what you meant.
        </p>

        {error !== null ? <ErrorMessage testId="session-remove-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={remove.isPending}
            onClick={onClose}
            testId="session-remove-cancel"
          >
            Keep it
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={remove.isPending}
            onClick={() => {
              setError(null);
              remove.mutate();
            }}
            testId="session-remove-confirm"
          >
            Remove
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
